/**
 * Product registry. Vertex is the backend behind several customer facing fronts,
 * and each front gets its own rescue button on its own hostname. This module is
 * what turns the hostname a request arrived on into the product it belongs to.
 *
 * Same rule as domains.ts and limits.ts: imported by BOTH the browser bundle and
 * the Pages Function, so no imports and no runtime globals. Only string
 * arithmetic. The Function must derive the product from the request `Host`,
 * never from anything the client can put in a body, so both runtimes have to
 * agree on this mapping exactly.
 *
 * Adding a front is adding one entry here plus one entry in the terraform
 * `products` map. Nothing else should need to change.
 */

export interface Product {
  /** Stable machine key. Ends up in the KV cooldown key, the OpsGenie alias, and tags, so changing it resets dedup state. */
  readonly key: string;
  /** What a human calls this product. Shown in the UI and in the alert message. */
  readonly name: string;
  /** Production hostname. One per product, and the terraform `products` map must match. */
  readonly hostname: string;
  /**
   * Optional per-product email allowlist. Unset means the shared list in
   * domains.ts applies, which is the right default: the allowlisted domains are
   * internal staff domains, not customer ones, so the same people support every
   * front. Set this only if one front should be reachable by a set of people the
   * others are not.
   */
  readonly allowedDomains?: readonly string[];
}

export const PRODUCTS: readonly Product[] = [
  {
    key: 'restolia',
    name: 'Restolia',
    hostname: 'rescue.restolia.com',
  },
  // Escolaria, the schools SaaS, is the next front to get a rescue button. Its
  // hostname lives in a different Cloudflare zone (cloudflare_schools_zone_id in
  // the vertex stack), which is why the terraform products map carries a zone id
  // per entry rather than assuming one zone.
  // {
  //   key: 'escolaria',
  //   name: 'Escolaria',
  //   hostname: 'rescue.escolaria.com',
  // },
];

/**
 * Used when the hostname matches nothing: local development, a *.pages.dev
 * preview, or a new hostname whose registry entry was forgotten.
 *
 * It falls back rather than refusing, because the alternative is a panic button
 * that answers "unknown host" while a restaurant is down. A page that reaches
 * on-call with a possibly wrong product label beats no page at all. The
 * fallback is logged by the Function so a missing entry is visible.
 */
export const FALLBACK_PRODUCT_KEY = 'restolia';

export interface ProductResolution {
  readonly product: Product;
  /** True when the hostname did not match any entry and the fallback was used. */
  readonly fellBack: boolean;
}

function normalizeHostname(hostname: string): string {
  // A Host header can legitimately carry a port. Strip it, and drop a trailing
  // dot from a fully qualified name. IPv6 literals are not a case this app has,
  // so the last colon is safe to cut on.
  let value = hostname.trim().toLowerCase();
  if (value.endsWith('.')) value = value.slice(0, -1);
  const colon = value.lastIndexOf(':');
  if (colon !== -1) value = value.slice(0, colon);
  return value;
}

/**
 * Resolve the product for a hostname. Exact match only, never a suffix test: a
 * suffix test would let `rescue.restolia.com.attacker.net` resolve to Restolia,
 * and while the product label is not an authorization decision, a mislabelled
 * P1 sends on-call to the wrong system during an outage.
 */
export function productForHostname(hostname: string | null | undefined): ProductResolution {
  const normalized = normalizeHostname(hostname ?? '');
  for (const product of PRODUCTS) {
    if (product.hostname === normalized) return { product, fellBack: false };
  }
  return { product: fallbackProduct(), fellBack: true };
}

export function fallbackProduct(): Product {
  for (const product of PRODUCTS) {
    if (product.key === FALLBACK_PRODUCT_KEY) return product;
  }
  // PRODUCTS is a non-empty literal and FALLBACK_PRODUCT_KEY names one of its
  // entries, so this is unreachable. It exists so the return type needs no
  // non-null assertion at every call site.
  return PRODUCTS[0];
}

/**
 * Env var holding the OpsGenie responder team for a product, e.g.
 * OPSGENIE_TEAM_RESTOLIA. The Function falls back to plain OPSGENIE_TEAM, so a
 * single-product deployment needs no per-product variable at all and a
 * multi-product one can route each front to its own team.
 */
export function teamEnvVarName(productKey: string): string {
  return `OPSGENIE_TEAM_${productKey.toUpperCase().replace(/[^A-Z0-9]/g, '_')}`;
}

/** KV key for the cooldown window. Per product, so one front's open alert never suppresses another front's page. */
export function cooldownKey(productKey: string): string {
  return `cooldown:${productKey}`;
}
